import React, { useMemo, useState, useEffect } from 'react';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { getBrandSections, brandDrawerInitialValues } from './content';
import { supervisorApiBaseUrl } from '@utils/env-utils';
import { DEFAULT_ERROR_TEXT } from '@utils';

const API_URL = `${supervisorApiBaseUrl}/api/Lookups/Insurance-Brands`;

interface IBrandDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingCodes: string[];
  brand?: any;
  isEdit?: boolean;
}

const BrandDrawer: React.FC<IBrandDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  existingCodes,
  brand,
  isEdit = false,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [values, setValues] = useState(brandDrawerInitialValues);

  useEffect(() => {
    if (isEdit && brand) {
      setValues({
        brandRepeater: [],
        code: brand?.BrandCode || '',
        title: brand?.Title || '',
      });
    } else {
      setValues({
        brandRepeater: [{ code: '', title: '' }],
        code: '',
        title: '',
      });
    }
  }, [brand, open, isEdit]);

  const sections = useMemo(
    () => getBrandSections(values, isEdit),
    [values, isEdit]
  );

  const handleChange = (
    fieldName: string,
    newValue: unknown,
    sectionIndex: number,
    allValues: any
  ) => {
    setValues({ ...allValues });
  };

  const submitForm = async (formValues: any) => {
    setSubmitButtonState('loading');
    setFormDisabled(true);

    const toLower = (val: string) => val?.toLowerCase?.() || '';

    if (isEdit) {
      const code = formValues.code;
      const title = formValues.title;
      const fullCode = `Brand${code}`;

      const currentCode = toLower(brand?.Code);
      const isDuplicate =
        toLower(fullCode) !== currentCode &&
        existingCodes.map(toLower).includes(toLower(fullCode));

      if (isDuplicate) {
        toast.error(
          <ToastErrorMessage>Brand already exists</ToastErrorMessage>
        );
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        return;
      }

      const body = {
        values: {
          Code: fullCode,
          BrandCode: code,
          Title: title,
        },
      };

      try {
        const response = await fetch(`${API_URL}/${brand.Code}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          setSubmitButtonState('success');
          toast.success(
            <ToastSuccessMessage>
              Brand updated successfully!
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setFormDisabled(false);
            setSubmitButtonState(undefined);
            onSuccess();
            onClose();
          }, 500);
        } else {
          let errorMessage = DEFAULT_ERROR_TEXT;
          try {
            const errorJson = await response.json();
            const referenceError = errorJson?.errors?.ReferenceError?.[0];
            if (
              typeof referenceError === 'string' &&
              referenceError.toLowerCase().includes('already exists')
            ) {
              errorMessage = 'Brand already exists';
            }
          } catch {
            <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>;
          }

          throw new Error(errorMessage);
        }
      } catch (err: any) {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>
            {err?.message || { DEFAULT_ERROR_TEXT }}
          </ToastErrorMessage>
        );
      }

      return;
    }

    const newBrands = formValues.brandRepeater || [];

    for (const brandRow of newBrands) {
      const code = brandRow.code;
      const fullCode = `Brand${code}`;

      if (existingCodes.map(toLower).includes(toLower(fullCode))) {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(
          <ToastErrorMessage>Brand already exists</ToastErrorMessage>
        );
        return;
      }
    }

    let allSuccess = true;

    for (const brandRow of newBrands) {
      const code = brandRow.code;
      const title = brandRow.title;
      const fullCode = `Brand${code}`;
      const body = {
        values: {
          Code: fullCode,
          BrandCode: code,
          Title: title,
        },
      };

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to save brand';
          try {
            const errorJson = await response.json();
            const referenceError = errorJson?.errors?.ReferenceError?.[0];
            if (
              typeof referenceError === 'string' &&
              referenceError.toLowerCase().includes('already exists')
            ) {
              errorMessage = 'Brand already exists';
            }
          } catch {
            // keep default message
          }

          allSuccess = false;
          toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
        }
      } catch {
        allSuccess = false;
        toast.error(
          <ToastErrorMessage>
            Server error while saving: {title}
          </ToastErrorMessage>
        );
      }
    }

    if (allSuccess) {
      setSubmitButtonState('success');
      toast.success(
        <ToastSuccessMessage>Lookup updated successfully!</ToastSuccessMessage>
      );
      setTimeout(() => {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        onSuccess();
        onClose();
      }, 500);
    } else {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
    }
  };

  return (
    <DynamicFormDrawer
      title={isEdit ? 'Edit Brand' : 'New Brand'}
      onClose={onClose}
      isOpen={open}
      onSubmit={submitForm}
      onChange={handleChange}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      sections={sections}
    />
  );
};

export default BrandDrawer;
